import { useEffect, useState } from "react";
import api, { formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Repeat, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", amount: "", category_id: "", account_id: "", day_of_month: 1 });

  const load = async () => {
    const [r, a, c] = await Promise.all([
      api.get("/recurring"), api.get("/accounts"), api.get("/categories"),
    ]);
    setItems(r.data); setAccounts(a.data); setCategories(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.amount || !form.category_id || !form.account_id) return toast.error("Champs requis");
    await api.post("/recurring", {
      name: form.name, amount: parseFloat(form.amount),
      category_id: form.category_id, account_id: form.account_id,
      day_of_month: parseInt(form.day_of_month), active: true,
    });
    toast.success("Facture récurrente ajoutée");
    setOpen(false); setForm({ name: "", amount: "", category_id: "", account_id: "", day_of_month: 1 });
    load();
  };

  const remove = async (id) => { await api.delete(`/recurring/${id}`); load(); };
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const total = items.filter((i) => i.active).reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Récurrent</h1>
          <p className="text-stone-500 mt-1 font-mono-num">Total mensuel : {formatEUR(total)}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="btn-add-recurring">
              <Plus className="w-4 h-4 mr-1" /> Récurrent
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Facture récurrente</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl h-11 mt-1.5" placeholder="Netflix" data-testid="rec-name-input" /></div>
              <div><Label>Montant (€)</Label><Input type="number" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl h-11 mt-1.5" data-testid="rec-amount-input" /></div>
              <div><Label>Jour du mois</Label><Input type="number" min="1" max="31" value={form.day_of_month}
                onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="rounded-xl h-11 mt-1.5" data-testid="rec-day-input" /></div>
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger className="rounded-xl h-11 mt-1.5" data-testid="rec-cat-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{categories.filter((c) => c.type === "expense").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compte</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger className="rounded-xl h-11 mt-1.5" data-testid="rec-account-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={submit} className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="rec-submit-button">
                Enregistrer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 rounded-2xl border-stone-200 text-center">
          <Repeat className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-400">Aucun paiement récurrent.</p>
        </Card>
      ) : (
        <Card className="rounded-2xl border-stone-200 overflow-hidden">
          <div className="divide-y divide-stone-100">
            {items.map((r) => {
              const cat = catMap[r.category_id];
              return (
                <div key={r.id} className="flex items-center gap-4 px-6 py-4" data-testid={`rec-${r.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Repeat className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-stone-500">Le {r.day_of_month} de chaque mois · {cat?.name}</div>
                  </div>
                  <div className="font-semibold text-rose-600 font-mono-num">{formatEUR(r.amount)}</div>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`rec-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
